import localforage from 'localforage';

// Create separate stores for different types of offline data
const apiCacheStore = localforage.createInstance({
  name: 'Tinat Academy',
  storeName: 'api_cache',
});

const offlineQueueStore = localforage.createInstance({
  name: 'Tinat Academy',
  storeName: 'offline_queue',
});

const appDataStore = localforage.createInstance({
  name: 'Tinat Academy',
  storeName: 'app_data',
});

// Cache duration constants
const CACHE_DURATION = {
  SHORT: 60 * 60 * 1000, // 1 hour
  MEDIUM: 24 * 60 * 60 * 1000, // 1 day
  LONG: 7 * 24 * 60 * 60 * 1000, // 7 days
};

/**
 * API Response Cache
 * Stores API responses for offline access
 */
export const apiCache = {
  /**
   * Store an API response in cache
   * @param {string} key - Cache key (usually the API endpoint)
   * @param {any} data - Response data to cache
   * @param {number} ttl - Time to live in milliseconds (default: 1 day)
   */
  async set(key, data, ttl = CACHE_DURATION.MEDIUM) {
    try {
      const cacheEntry = {
        data,
        timestamp: Date.now(),
        ttl,
        expiresAt: Date.now() + ttl,
      };
      await apiCacheStore.setItem(key, cacheEntry);
      return true;
    } catch (error) {
      // console.error('Error caching API response:', error);
      return false;
    }
  },

  /**
   * Get a cached API response
   * @param {string} key - Cache key
   * @returns {any|null} - Cached data or null if not found/expired
   */
  async get(key) {
    try {
      const cacheEntry = await apiCacheStore.getItem(key);
      if (!cacheEntry) {
        return null;
      }

      // Check if cache is expired
      if (Date.now() > cacheEntry.expiresAt) {
        await apiCacheStore.removeItem(key);
        return null;
      }

      return cacheEntry.data;
    } catch (error) {
      // console.error('Error retrieving cached API response:', error);
      return null;
    }
  },

  /**
   * Remove a specific cache entry
   * @param {string} key - Cache key to remove
   */
  async remove(key) {
    try {
      await apiCacheStore.removeItem(key);
    } catch (error) {
      // console.error('Error removing cache entry:', error);
    }
  },

  /**
   * Clear all expired cache entries
   */
  async clearExpired() {
    try {
      const keys = await apiCacheStore.keys();
      const now = Date.now();
      
      for (const key of keys) {
        const entry = await apiCacheStore.getItem(key);
        if (entry && now > entry.expiresAt) {
          await apiCacheStore.removeItem(key);
        }
      }
    } catch (error) {
      // console.error('Error clearing expired cache:', error);
    }
  },

  /**
   * Clear all cache entries
   */
  async clear() {
    try {
      await apiCacheStore.clear();
    } catch (error) {
      // console.error('Error clearing cache:', error);
    }
  },

  /**
   * Get cache statistics
   */
  async getStats() {
    try {
      const keys = await apiCacheStore.keys();
      const now = Date.now();
      let totalSize = 0;
      let expiredCount = 0;
      let validCount = 0;

      for (const key of keys) {
        const entry = await apiCacheStore.getItem(key);
        if (entry) {
          const entrySize = JSON.stringify(entry).length;
          totalSize += entrySize;
          
          if (now > entry.expiresAt) {
            expiredCount++;
          } else {
            validCount++;
          }
        }
      }

      return {
        totalEntries: keys.length,
        validEntries: validCount,
        expiredEntries: expiredCount,
        estimatedSize: totalSize,
      };
    } catch (error) {
      // console.error('Error getting cache stats:', error);
      return null;
    }
  },
};

/**
 * Offline Request Queue
 * Queues API requests when offline to sync when back online
 */
export const offlineQueue = {
  /**
   * Add a request to the offline queue
   * @param {string} id - Unique request ID
   * @param {object} request - Request data { method, url, data, headers }
   */
  async add(id, request) {
    try {
      const queueItem = {
        id,
        request,
        timestamp: Date.now(),
        retries: 0,
      };
      await offlineQueueStore.setItem(id, queueItem);
      return true;
    } catch (error) {
      // console.error('Error adding to offline queue:', error);
      return false;
    }
  },

  /**
   * Get all queued requests
   * @returns {Array} - Array of queued requests
   */
  async getAll() {
    try {
      const keys = await offlineQueueStore.keys();
      const queue = [];
      
      for (const key of keys) {
        const item = await offlineQueueStore.getItem(key);
        if (item) {
          queue.push(item);
        }
      }
      
      // Sort by timestamp (oldest first)
      return queue.sort((a, b) => a.timestamp - b.timestamp);
    } catch (error) {
      // console.error('Error getting offline queue:', error);
      return [];
    }
  },

  /**
   * Remove a request from the queue
   * @param {string} id - Request ID to remove
   */
  async remove(id) {
    try {
      await offlineQueueStore.removeItem(id);
    } catch (error) {
      // console.error('Error removing from offline queue:', error);
    }
  },

  /**
   * Clear all queued requests
   */
  async clear() {
    try {
      await offlineQueueStore.clear();
    } catch (error) {
      // console.error('Error clearing offline queue:', error);
    }
  },

  /**
   * Get queue statistics
   */
  async getStats() {
    try {
      const queue = await this.getAll();
      return {
        count: queue.length,
        oldestRequest: queue.length > 0 ? queue[0].timestamp : null,
        newestRequest: queue.length > 0 ? queue[queue.length - 1].timestamp : null,
      };
    } catch (error) {
      // console.error('Error getting queue stats:', error);
      return null;
    }
  },
};

/**
 * App Data Storage
 * Stores application state and user data for offline access
 */
export const appData = {
  /**
   * Store application data
   * @param {string} key - Data key
   * @param {any} value - Data value
   */
  async set(key, value) {
    try {
      await appDataStore.setItem(key, value);
      return true;
    } catch (error) {
      // console.error('Error storing app data:', error);
      return false;
    }
  },

  /**
   * Get application data
   * @param {string} key - Data key
   * @returns {any|null} - Stored data or null
   */
  async get(key) {
    try {
      return await appDataStore.getItem(key);
    } catch (error) {
      // console.error('Error retrieving app data:', error);
      return null;
    }
  },

  /**
   * Remove application data
   * @param {string} key - Data key to remove
   */
  async remove(key) {
    try {
      await appDataStore.removeItem(key);
    } catch (error) {
      // console.error('Error removing app data:', error);
    }
  },

  /**
   * Clear all application data
   */
  async clear() {
    try {
      await appDataStore.clear();
    } catch (error) {
      // console.error('Error clearing app data:', error);
    }
  },
};

/**
 * Storage Management Utilities
 */
export const storageUtils = {
  /**
   * Get total storage usage estimate
   * @returns {Promise<object>} - Storage statistics
   */
  async getStorageInfo() {
    try {
      const [apiStats, queueStats] = await Promise.all([
        apiCache.getStats(),
        offlineQueue.getStats(),
      ]);

      // Estimate storage usage (rough calculation)
      let totalSize = 0;
      if (apiStats) totalSize += apiStats.estimatedSize || 0;

      return {
        apiCache: apiStats,
        offlineQueue: queueStats,
        estimatedTotalSize: totalSize,
        estimatedTotalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
      };
    } catch (error) {
      // console.error('Error getting storage info:', error);
      return null;
    }
  },

  /**
   * Check if storage quota is available
   * @returns {Promise<boolean>} - True if storage is available
   */
  async checkStorageQuota() {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        const usage = estimate.usage || 0;
        const quota = estimate.quota || 0;
        const usagePercent = (usage / quota) * 100;
        
        // Warn if usage is above 80%
        if (usagePercent > 80) {
          // console.warn(`Storage usage is at ${usagePercent.toFixed(2)}%`);
        }
        
        return usagePercent < 95; // Return false if above 95%
      }
      return true; // Assume available if API not supported
    } catch (error) {
      // console.error('Error checking storage quota:', error);
      return true;
    }
  },

  /**
   * Clean up old/expired data
   * @param {boolean} includeQueue - Whether to clear offline queue
   */
  async cleanup(includeQueue = false) {
    try {
      await apiCache.clearExpired();
      if (includeQueue) {
        await offlineQueue.clear();
      }
      return true;
    } catch (error) {
      // console.error('Error during cleanup:', error);
      return false;
    }
  },
};

/**
 * Offline Detection Helper
 */
export const offlineHelper = {
  /**
   * Check if browser is currently offline
   * @returns {boolean}
   */
  isOffline() {
    return !navigator.onLine;
  },

  /**
   * Check if browser supports service workers
   * @returns {boolean}
   */
  supportsServiceWorker() {
    return 'serviceWorker' in navigator;
  },

  /**
   * Check if browser supports IndexedDB (required for localforage)
   * @returns {boolean}
   */
  supportsIndexedDB() {
    return 'indexedDB' in window;
  },

  /**
   * Get offline capabilities status
   * @returns {object}
   */
  getCapabilities() {
    return {
      isOffline: this.isOffline(),
      supportsServiceWorker: this.supportsServiceWorker(),
      supportsIndexedDB: this.supportsIndexedDB(),
      canWorkOffline: this.supportsServiceWorker() && this.supportsIndexedDB(),
    };
  },
};

// Initialize: Clear expired cache on load
if (typeof window !== 'undefined') {
  // Run cleanup in background
  setTimeout(() => {
    storageUtils.cleanup().catch(console.error);
  }, 5000);
}

export default {
  apiCache,
  offlineQueue,
  appData,
  storageUtils,
  offlineHelper,
  CACHE_DURATION,
};

